/**
 * AR Analytics Tracking
 * Tracks user interactions with AR experiences and sends events to GA4
 */

(function() {
  "use strict";

  // Session tracking flags and timing
  const sessions = {};

  // Detect if user arrived via QR code scan (UTM param set by pcBuildARUrl)
  const urlParams = new URLSearchParams(window.location.search);
  const arSource = urlParams.get('utm_source') === 'qr_code' ? 'qr_scan' : 'direct_mobile';

  // Pending AR launches — tracks timeout between button click and session start
  const pendingAR = {};

  // Main tracking function for AR events
  window.trackAREvent = function(eventName, productInfo, additionalParams = {}) {
    if (typeof gtag === 'undefined') return;
    
    // Combine product info with additional parameters
    const eventParams = {
      product_id: productInfo.id || '',
      variant_id: productInfo.variantId || '',
      product_title: productInfo.title || '',
      product_price: productInfo.price || '',
      model_url: productInfo.modelUrl || '',
      ...additionalParams
    };
    
    // Send to GA4
    gtag('event', eventName, eventParams);
  };
  
  // Initialize tracking on models when page loads
  document.addEventListener('DOMContentLoaded', function() {
    initARTracking();
  });
  
  // Track AR interactions for all model viewers on page
  function initARTracking() {
    // Find all model-viewer elements
    document.querySelectorAll('model-viewer').forEach(function(viewer) {
      setupModelViewerTracking(viewer);
    });
    
    // Track QR code interactions if they exist
    setupQRCodeTracking();
  }
  
  // Set up tracking for a specific model viewer
  function setupModelViewerTracking(modelViewer) {
    if (!modelViewer) return;
    
    // Get product data from context
    const productInfo = getProductInfoFromContext(modelViewer);
    const modelViewerId = modelViewer.id || 'unknown-model';
    
    // Track when AR button is clicked + funnel diagnostics
    const arButton = modelViewer.querySelector('button[slot="ar-button"]');
    if (arButton) {
      arButton.addEventListener('click', function() {
        trackAREvent('ar_button_click', productInfo, { ar_source: arSource });

        // Check device AR support via model-viewer's canActivateAR property
        if (typeof modelViewer.canActivateAR !== 'undefined' && !modelViewer.canActivateAR) {
          trackAREvent('ar_device_unsupported', productInfo, { ar_source: arSource });
          return;
        }

        // Start a timeout — if no session-started within 15s, fire ar_session_timeout
        pendingAR[modelViewerId] = setTimeout(function() {
          trackAREvent('ar_session_timeout', productInfo, { ar_source: arSource });
          delete pendingAR[modelViewerId];
        }, 15000);
      });
    }
    
    // Listen for the ar-status event
    modelViewer.addEventListener('ar-status', function(event) {
      const status = event.detail.status;
      
      if (status === 'session-started') {
        // Clear the timeout — session started successfully
        if (pendingAR[modelViewerId]) {
          clearTimeout(pendingAR[modelViewerId]);
          delete pendingAR[modelViewerId];
        }

        sessions[modelViewerId] = {
          startTime: Date.now(),
          productInfo: productInfo
        };

        trackAREvent('ar_session_started', productInfo, {
          ar_source: arSource,
          placement: modelViewer.getAttribute('ar-placement') || 'unknown'
        });
      } else if (status === 'session-ended') {
        if (sessions[modelViewerId] && sessions[modelViewerId].startTime) {
          const sessionDuration = Math.round((Date.now() - sessions[modelViewerId].startTime) / 1000);
          trackAREvent('ar_session_ended', productInfo, {
            ar_source: arSource,
            session_duration_seconds: sessionDuration
          });
          delete sessions[modelViewerId];
        } else {
          trackAREvent('ar_session_ended', productInfo, { ar_source: arSource });
        }
      } else if (status === 'failed') {
        // Clear the timeout on failure too
        if (pendingAR[modelViewerId]) {
          clearTimeout(pendingAR[modelViewerId]);
          delete pendingAR[modelViewerId];
        }

        trackAREvent('ar_session_failed', productInfo, {
          ar_source: arSource,
          reason: event.detail.reason || 'unknown'
        });
      } else if (status === 'not-presenting') {
        if (sessions[modelViewerId] && sessions[modelViewerId].startTime) {
          const sessionDuration = Math.round((Date.now() - sessions[modelViewerId].startTime) / 1000);
          trackAREvent('ar_session_exited', productInfo, {
            ar_source: arSource,
            session_duration_seconds: sessionDuration
          });
          delete sessions[modelViewerId];
        }
      }
    });
    
    // iOS AR Quick Look tracking
    // model-viewer's ar-status doesn't fire reliably on iOS because Safari
    // hands off to the native AR Quick Look viewer (outside browser control).
    // We use quick-look-button-tapped as session start, and visibilitychange
    // (page regains focus when user exits AR) as session exit.
    modelViewer.addEventListener('quick-look-button-tapped', function() {
      trackAREvent('ar_quick_look_tapped', productInfo, { ar_source: arSource });

      // Clear the timeout — Quick Look launch counts as session start
      if (pendingAR[modelViewerId]) {
        clearTimeout(pendingAR[modelViewerId]);
        delete pendingAR[modelViewerId];
      }

      // Only start an iOS session if we don't already have one from ar-status
      if (!sessions[modelViewerId]) {
        sessions[modelViewerId] = {
          startTime: Date.now(),
          productInfo: productInfo,
          platform: 'ios'
        };
        trackAREvent('ar_session_started', productInfo, { ar_source: arSource, platform: 'ios' });

        function onReturn() {
          if (document.hidden) return;
          document.removeEventListener('visibilitychange', onReturn);
          if (sessions[modelViewerId] && sessions[modelViewerId].platform === 'ios') {
            const duration = Math.round((Date.now() - sessions[modelViewerId].startTime) / 1000);
            trackAREvent('ar_session_exited', productInfo, {
              ar_source: arSource,
              session_duration_seconds: duration,
              platform: 'ios'
            });
            delete sessions[modelViewerId];
          }
        }
        document.addEventListener('visibilitychange', onReturn);
      }
    });
  }
  
  // Set up tracking for QR code interactions
  function setupQRCodeTracking() {
    // PCModelQR is the only live QR container (rendered in product-images.liquid)
    const qrOverlay = document.getElementById('PCModelQR');
    if (!qrOverlay) return;

    const productInfo = getProductInfoFromPage();

    // Track meaningful QR interaction — fires once per page view when user
    // hovers (desktop) or taps (touch) the QR code area, indicating intent to scan
    let interacted = false;
    function trackInteraction(method) {
      if (interacted) return;
      interacted = true;
      trackAREvent('qr_code_interacted', productInfo, { method: method });
    }

    qrOverlay.addEventListener('mouseenter', function() { trackInteraction('hover'); });
    qrOverlay.addEventListener('click', function() { trackInteraction('click'); });
  }
  
  // Helper function to get product info from the page context
  function getProductInfoFromContext(element) {
    // Try to find product form
    const productForm = element.closest('form[action*="/cart/add"]');
    let productInfo = {};
    
    if (productForm) {
      const productInput = productForm.querySelector('input[name="id"]');
      const productJson = document.querySelector('[data-product-json]');
      
      if (productJson) {
        try {
          const productData = JSON.parse(productJson.textContent);
          productInfo = {
            id: productData.id,
            variantId: productInput ? productInput.value : '',
            title: productData.title,
            price: productData.price,
            modelUrl: element.getAttribute('src') || ''
          };
        } catch (e) {
          console.error('[AR Analytics] Error parsing product JSON', e);
        }
      } else {
        // Fallback to attributes on the model-viewer
        const modelViewerId = element.id || '';
        const variantId = modelViewerId.replace('model-viewer-', '');
        
        productInfo = {
          variantId: variantId,
          modelUrl: element.getAttribute('src') || ''
        };
      }
    }
    
    return productInfo;
  }
  
  // Get product info from the page
  function getProductInfoFromPage() {
    const productJson = document.querySelector('[data-product-json]');
    let productInfo = {};
    
    if (productJson) {
      try {
        const productData = JSON.parse(productJson.textContent);
        productInfo = {
          id: productData.id,
          title: productData.title,
          price: productData.price
        };
        
        // Try to get current variant
        const variantInput = document.querySelector('input[name="id"]');
        if (variantInput) {
          productInfo.variantId = variantInput.value;
        }
      } catch (e) {
        console.error('[AR Analytics] Error parsing product JSON', e);
      }
    }
    
    return productInfo;
  }
})();
